import api from "./client";


export async function buscarUsuarios() {
	const response = await api.get(`/usuario`);
	return response.data;
}


// Buscar usuários compatíveis com a função de uma tarefa
export async function buscarUsuariosParaTarefa(tarefaId: number) {
	try {
		const response = await api.get(`/usuario/${tarefaId}/usuarios-para-tarefa`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar usuários para tarefa:", error);
		// Extrai a mensagem do erro do backend se disponível
		// NestJS retorna a mensagem em error.response.data.message
		const mensagem =
			error?.response?.data?.message ||
			error?.response?.data?.error ||
			error?.message ||
			"Erro ao buscar usuários para tarefa";

		// Criar um erro com a mensagem extraída para manter a compatibilidade
		const erro = new Error(mensagem);
		// Preservar a resposta original para acesso no catch
		(erro as any).response = error.response;
		throw erro;
	}
}


export async function excluirUsuario(id: number): Promise<void> {
	try {
		await api.delete(`/usuario/${id}`);
	} catch (error) {
		console.error("Erro ao excluir usuário:", error);
		throw error;
	}
}


export async function atualizarUsuario(
	id: number,
	dados: {
		responsavel?: string;
		usuario?: string;
		senha?: string;
		cpf?: string;
		ativo?: boolean;
		perfil_id?: number;
		armazens_ids?: number[];
	},
): Promise<any> {
	try {
		const response = await api.patch(`/usuario/${id}`, dados);
		return response.data;
	} catch (error) {
		console.error("Erro ao atualizar usuário:", error);
		throw error;
	}
}


export async function buscarUsuarioPorId(id: number): Promise<any> {
	try {
		const response = await api.get(`/usuario/${id}`);
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar usuário:", error);
		throw error;
	}
}


// Função para verificar se um CPF já existe no sistema
export async function verificarCpfDuplicado(
	cpf: string,
	usuarioId?: number,
): Promise<boolean> {
	try {
		// Buscar todos os usuários
		const usuarios = await buscarUsuarios();

		// Verificar se existe um usuário com o mesmo CPF
		const cpfLimpo = cpf.replace(/\D/g, ""); // Remove formatação

		const usuarioComMesmoCpf = usuarios.find((u: any) => {
			const cpfUsuarioLimpo = u.cpf?.replace(/\D/g, "") || "";

			// Se estiver editando, não considerar o próprio usuário
			if (usuarioId && u.usuario_id === usuarioId) {
				return false;
			}

			return cpfUsuarioLimpo === cpfLimpo;
		});

		return !!usuarioComMesmoCpf; // Retorna true se encontrar CPF duplicado
	} catch (error) {
		console.error("Erro ao verificar CPF duplicado:", error);
		throw error;
	}
}


// ---------- PERFIL FUNCTIONS ----------
export interface Perfil {
	perfil_id: number;
	nome: string;
	descricao?: string;
	pode_ver: boolean;
	pode_add: boolean;
	pode_edit: boolean;
	pode_delete: boolean;
	usuarios_count?: number;
}


export async function buscarPerfis(): Promise<PerfilBackend[]> {
	try {
		const response = await api.get("/perfil");
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao buscar perfis:",
			error.response?.data || error.message,
		);
		throw new Error("Falha ao carregar os perfis.");
	}
}


export async function criarPerfil(dados: {
	nome: string;
	descricao?: string;
	funcao?: string[];
	funcoes_lider?: string[];
}): Promise<PerfilBackend> {
	try {
		const response = await api.post("/perfil", dados);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao criar perfil:", error.message);
		throw new Error("Falha ao criar o perfil.");
	}
}


export async function atualizarPerfil(
	id: number,
	dados: {
		nome: string;
		descricao?: string;
		funcao?: string[];
		funcoes_lider?: string[];
	},
): Promise<PerfilBackend> {
	try {
		const response = await api.patch(`/perfil/${id}`, dados);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atualizar perfil:", error.message);
		throw new Error("Falha ao atualizar o perfil.");
	}
}


export async function excluirPerfil(id: number): Promise<void> {
	try {
		await api.delete(`/perfil/${id}`);
	} catch (error: any) {
		console.error("Erro ao excluir perfil:", error.message);
		throw new Error("Falha ao excluir o perfil.");
	}
}


// ========== PERMISSÕES ==========

// Interface para permissão do backend
export interface PermissaoBackend {
	permissao_id: number;
	modulo: //WMS MODULOS PERMISSOES
		| "dashboard"
		| "armazem"
		| "tipo_localizacao"
		| "localizacao"
		| "produto"
		| "movimentacao"
		| "transferencia"
		| "ocorrencia"
		//| "conferencia"
		//| "mapeamento"
		| "separacao"
		| "auditoria"
		| "relatorio"
		| "usuario"
		| "perfil"
		| "balanco"
		| "inventario"
		| "nota_fiscal"
		| "conferencia_nota"
		| "ocorrencia_nf"
		//OMS MODULOS PERMISSOES
		| "oms_dashboard"
		| "oms_tarefas"
		| "oms_reposicao"
		| "oms_coleta_reposicao"
		| "oms_romaneio"
		| "oms_embalagem"
		| "oms_pedidos"
		| "oms_mesas_separacao"
		| "oms_horarios_separadores"
		| "oms_relatorios"
		| "oms_trocas"
		| "oms_full"
		| "oms_coleta_full"
		| "oms_conferencia_full"
		| "oms_comercial";

	pode_ver: boolean;
	pode_incluir: boolean;
	pode_editar: boolean;
	pode_excluir: boolean;
}


// Interface para perfil do backend
export interface PerfilBackend {
	perfil_id: number;
	nome: string;
	descricao?: string;
	funcao?: string[];
	funcoes_lider?: string[];
	permissoes: PermissaoBackend[];
}


// Buscar todas as permissões disponíveis
export async function buscarPermissoes(): Promise<PermissaoBackend[]> {
	try {
		const response = await api.get("/permissao");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar permissões:", error.message);
		throw new Error("Falha ao carregar as permissões.");
	}
}


// Buscar permissões por módulo
export async function buscarPermissoesPorModulo(
	modulo: string,
): Promise<PermissaoBackend[]> {
	try {
		const response = await api.get(`/permissao/modulo/${modulo}`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar permissões por módulo:", error.message);
		throw new Error("Falha ao carregar as permissões do módulo.");
	}
}


// Criar permissões padrão
export async function criarPermissoesPadrao(): Promise<PermissaoBackend[]> {
	try {
		const response = await api.post("/permissao/criar-padrao");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao criar permissões padrão:", error.message);
		throw new Error("Falha ao criar permissões padrão.");
	}
}


// Buscar perfil por ID com permissões
export async function buscarPerfilPorId(id: number): Promise<PerfilBackend> {
	try {
		const response = await api.get(`/perfil/${id}`);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao buscar perfil:",
			error.response?.data || error.message,
		);
		if (error.response?.status === 404) {
			throw new Error("Perfil não encontrado.");
		}
		throw new Error("Falha ao carregar o perfil.");
	}
}


// Adicionar permissões ao perfil
export async function adicionarPermissoesAoPerfil(
	perfilId: number,
	permissaoIds: number[],
): Promise<PerfilBackend> {
	try {
		const response = await api.post(`/perfil/${perfilId}/permissoes`, {
			permissao_ids: permissaoIds,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao adicionar permissões ao perfil:", error.message);
		throw new Error("Falha ao adicionar permissões ao perfil.");
	}
}


// Remover permissões do perfil
export async function removerPermissoesDoPerfil(
	perfilId: number,
	permissaoIds: number[],
): Promise<PerfilBackend> {
	try {
		const response = await api.delete(`/perfil/${perfilId}/permissoes`, {
			data: { permissao_ids: permissaoIds },
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao remover permissões do perfil:", error.message);
		throw new Error("Falha ao remover permissões do perfil.");
	}
}


// Atualizar permissão individual
export async function atualizarPermissao(
	id: number,
	dados: {
		pode_ver?: boolean;
		pode_incluir?: boolean;
		pode_editar?: boolean;
		pode_excluir?: boolean;
	},
): Promise<PermissaoBackend> {
	try {
		const response = await api.patch(`/permissao/${id}`, dados);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atualizar permissão:", error.message);
		throw new Error("Falha ao atualizar permissão.");
	}
}


// Criar nova permissão
export async function criarPermissao(dados: {
	modulo: //WMS MODULOS PERMISSOES
		| "dashboard"
		| "armazem"
		| "tipo_localizacao"
		| "localizacao"
		| "produto"
		| "movimentacao"
		| "transferencia"
		| "ocorrencia"
		//| "conferencia"
		| "conferencia_localizacao"
		//| "mapeamento"
		| "separacao"
		| "auditoria"
		| "relatorio"
		| "usuario"
		| "perfil"
		| "balanco"
		| "inventario"
		| "nota_fiscal"
		| "conferencia_nota"
		| "ocorrencia_nf"
		//OMS MODULOS PERMISSOES
		| "oms_dashboard"
		| "oms_tarefas"
		| "oms_reposicao"
		| "oms_coleta_reposicao"
		| "oms_romaneio"
		| "oms_embalagem"
		| "oms_pedidos"
		| "oms_mesas_separacao"
		| "oms_horarios_separadores"
		| "oms_relatorios"
		| "oms_trocas"
		| "oms_full"
		| "oms_coleta_full"
		| "oms_conferencia_full"
		| "oms_comercial";
	pode_ver: boolean;
	pode_incluir: boolean;
	pode_editar: boolean;
	pode_excluir: boolean;
}): Promise<PermissaoBackend> {
	try {
		const response = await api.post("/permissao", dados);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao criar permissão:",
			error.response?.data || error.message,
		);
		throw new Error("Falha ao criar permissão.");
	}
}


// Buscar usuários por perfil
export async function buscarUsuariosPorPerfil(
	perfilId: number,
): Promise<any[]> {
	try {
		const response = await api.get("/usuario");
		const todosUsuarios = response.data;
		// Filtrar usuários que têm o perfil especificado
		return todosUsuarios.filter(
			(usuario: any) => usuario.perfil?.perfil_id === perfilId,
		);
	} catch (error: any) {
		console.error("Erro ao buscar usuários por perfil:", error.message);
		throw new Error("Falha ao carregar usuários do perfil.");
	}
}


// Definir permissões do perfil (apenas associar IDs, sem modificar valores globais)
export async function definirPermissoesDoPerfil(
	perfilId: number,
	permissaoIds: number[],
): Promise<PerfilBackend> {
	try {
		// Apenas associar as permissões ao perfil (não modifica valores globais)
		const response = await api.patch(`/perfil/${perfilId}/permissoes`, {
			permissao_ids: permissaoIds,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao definir permissões do perfil:", error.message);
		throw new Error("Falha ao definir permissões do perfil.");
	}
}

